import { makeVar, useReactiveVar } from '@apollo/client';
import {
  FetchQuotationUserRfqsQuery,
  ProjectStatusType,
  useFetchQuotationUserRfqsQuery
} from '@generated/graphql';
import useIntersectionObserver, {
  CallBackRef
} from '@hooks/useIntersectionObserver';
import { useSelector } from '@store';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useState
} from 'react';
import SharedLinkBoundary from './SharedLinkBoundary';
import useBrowserVisiblity from '@hooks/useBrowserVisiblity';

interface ChildrenProps {
  data?: FetchQuotationUserRfqsQuery;
  loading: boolean;
  callbackRef: CallBackRef<HTMLDivElement>;
  setSearchParam: Dispatch<
    SetStateAction<{
      searchKeyword?: string | null;
      searchType?: ProjectStatusType | null;
    }>
  >;
  noSearchResult: boolean;
}

interface Props {
  limit?: number;
  offset?: number;
  children: (props: ChildrenProps) => ReactNode;
}

const LIMIT = 30;

export const paginationVar = makeVar({
  limit: 30,
  page: 1
});

const RequestListProvider = ({ children }: Props) => {
  const pagination = useReactiveVar(paginationVar);
  const [searchParam, setSearchParam] = useState<{
    searchKeyword?: string | null;
    searchType?: ProjectStatusType | null;
  }>({
    searchKeyword: '',
    searchType: ProjectStatusType.All
  });

  const { page } = pagination;

  const accessToken = useSelector((state) => state.client.accessToken);

  const { data, loading, refetch, fetchMore } = useFetchQuotationUserRfqsQuery({
    skip: !accessToken,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        limit: LIMIT,
        offset: 0,
        ...searchParam
      }
    }
  });

  const count = data?.fetchQuotationUserRfqs.data?.count ?? 0;
  const totalPage = Math.ceil(count / LIMIT);
  const noSearchResult =
    (data?.fetchQuotationUserRfqs?.data?.rfqs ?? []).length === 0 &&
    ((searchParam.searchKeyword ?? '').length > 0 ||
      searchParam.searchType !== ProjectStatusType.All);

  const {
    refetch: fetchQuotationUserRfqs,
    startPolling,
    stopPolling
  } = useFetchQuotationUserRfqsQuery({
    skip: !data,
    variables: {
      input: {
        limit: totalPage === page ? count : page * LIMIT,
        offset: 0,
        ...searchParam
      }
    }
  });

  useBrowserVisiblity({
    onVisible: () => {
      fetchQuotationUserRfqs();
      startPolling(3000);
    },
    onInVisible: () => stopPolling()
  });

  const refetchHadnler = useCallback(() => {
    return refetch({
      input: {
        limit: page * LIMIT,
        offset: 0,
        ...searchParam
      }
    });
  }, [page, refetch, searchParam]);

  const intersectHandler = () => {
    totalPage - 1 >= page &&
      paginationVar({ ...pagination, page: pagination.page + 1 });

    fetchMore({
      variables: {
        input: {
          limit: LIMIT,
          offset: page * LIMIT,
          ...searchParam
        }
      }
    });
  };

  const { callbackRef } = useIntersectionObserver<HTMLDivElement>({
    callback: intersectHandler,
    option: {
      threshold: 0
    }
  });

  return (
    <SharedLinkBoundary refetch={refetchHadnler}>
      {children({ data, loading, callbackRef, setSearchParam, noSearchResult })}
    </SharedLinkBoundary>
  );
};

export default RequestListProvider;
